import React from 'react';
import { useNavigate } from 'react-router-dom';

import { StyleSheet, Text, View } from './base_components';
import { BusyOverlay, useBusy } from './busy_component';
import StandardBoxList from './standard_box_list';
import LinkTextButton from './buttons/link_text_button';
import { useShowMode, ShowModeSelect } from './show_mode_select';
import TextButton from './buttons/text_button';
import TitleActionLine from './title_action_line';

import DirStore from '../stores/dir_store';
import MediaStore from '../stores/media_store';

import { hasMultiDownload } from '../lib/volume_dir_helper';
import { byteFormat, dateFormat, stopAll } from '../tools/util';

const styles = StyleSheet.create({
  noMedia: {
    margin: '2rem',
    color: '#555',
    fontSize: '1.5rem',
    fontWeight: '600',
  },
  deletedText: {
    flex: 1,
    color: 'red',
    fontSize: '1.5rem',
    lineHeight: '2.5rem',
    fontWeight: '500',
    wordBreak: 'break-all',
    overflowWrap: 'break-word',
    textDecoration: 'underline',
  },
  actionButton: { marginLeft: '1rem' },
  spacer: { flex: 1 },
});

type Props = { org: any; mediaGrant: any; path: string; sourceDir: any[] };
export default function MediaDirList(props: Props) {
  const { org, path, sourceDir: source_dir } = props;
  const media_grant_id = props.mediaGrant?.media_grant_id ?? 0;
  const grant_name = props.mediaGrant?.grant_name ?? '';
  const [is_busy, setBusy, clearBusy] = useBusy();
  const show_mode = useShowMode();
  const [selected, setSelected] = React.useState([]);
  const navigate = useNavigate();
  const show_del = show_mode !== 'active_files';
  const show_active = show_mode !== 'deleted_files';

  React.useEffect(() => {
    setSelected([]);
  }, [grant_name, path, show_mode]);

  let content: any = null;
  let rows: any;
  const media_list = _filterList(
    source_dir,
    path,
    grant_name,
    show_del,
    show_active
  );
  if (media_list.length > 0) {
    rows = media_list.map((item) => {
      const { is_deleted } = item;
      return {
        url: item.path,
        list: [
          is_deleted ? <Deleted text={item.name} /> : item.name,
          item.media ? byteFormat(item.media.file_size) : 'Directory',
          dateFormat(item.media?.last_updated),
        ],
        //disable_checkbox: !item.media,
      };
    });
  } else {
    content = <Text style={styles.noMedia}>Empty Directory</Text>;
  }
  const select_media = selected.map((i) => media_list[i]).filter((i) => i);
  const delete_list = select_media
    .filter((item) => item.is_deleted && item.media)
    .map((i) => i.media);
  const active_list = select_media
    .filter((item) => !item.is_deleted && item.media)
    .map((i) => i.media);
  const dir_list = select_media.filter((item) => !item.media);
  const dir_active_list = select_media.filter(
    (item) => !item.is_deleted && !item.media
  );
  const dir_inactive_list = select_media.filter(
    (item) => item.is_deleted && !item.media
  );

  function _onProgress() {}
  function _onDeletePress() {
    if (dir_list.length > 0) {
      const state = { media_list: active_list, dir_list };
      navigate(`/bulk/delete/${grant_name}${path}`, { state });
    } else {
      const yes = window.confirm(
        `Are you sure want to delete ${active_list.length} items?`
      );
      if (yes && setBusy()) {
        const opts = {
          org_id: org?.org_id,
          media_grant_id,
          media_list: active_list,
          on_progress: _onProgress,
        };
        MediaStore.deleteList(opts, (err) => {
          if (err) {
            window.alert('Delete failed, please try again.');
          }
          DirStore.fetch({ org_id: org?.org_id, media_grant_id, path });
          clearBusy();
        });
      }
    }
  }
  function _onUndeletePress() {
    if (dir_list.length > 0) {
      const state = { media_list: delete_list, dir_list };
      navigate(`/bulk/undelete/${grant_name}${path}`, { state });
    } else {
      const yes = window.confirm(
        `Are you sure want to restore ${delete_list.length} items?`
      );
      if (yes && setBusy()) {
        const opts = {
          org_id: org?.org_id,
          media_grant_id,
          media_list: delete_list,
          on_progress: _onProgress,
        };
        MediaStore.undeleteList(opts, (err) => {
          if (err) {
            window.alert('Restore failed, please try again.');
          }
          DirStore.fetch({ org_id: org?.org_id, media_grant_id, path });
          clearBusy();
        });
      }
    }
  }
  function _onDownloadPress() {
    if (!org.cap_web_download) {
      window.alert('Sorry, you do not have permission to download files.');
    } else if (active_list.length === 1 && dir_active_list.length === 0) {
      _singleDownload(active_list[0].media_id);
    } else if (hasMultiDownload()) {
      const state = { media_list: active_list, dir_list: dir_active_list };
      navigate(`/download/${grant_name}${path}`, { state });
    } else {
      window.alert('Sorry, your browser does not support multi-download.');
    }
  }
  function _singleDownload(media_id) {
    if (setBusy()) {
      const opts = {
        org_id: org?.org_id,
        media_grant_id,
        media_id,
        inline: false,
      };
      MediaStore.getUrl(opts, (err, url) => {
        clearBusy();
        if (err === 404) {
          window.alert('File not uploaded, check back later.');
        } else if (err === 403) {
          window.alert(
            'Sorry, you do not have permission to download this file.'
          );
        } else if (err) {
          window.alert('Failed to get download url, please try again.');
        } else {
          _downloadFile(url);
        }
      });
    }
  }
  function _onUploadPress(e: any) {
    if (!org.cap_web_upload) {
      stopAll(e);
      window.alert('Sorry, you do not have permission to upload.');
    }
    return org.cap_web_upload ? true : false;
  }

  const buttons = (
    <>
      <ShowModeSelect />
      <View style={styles.spacer} />
      <TextButton
        style={styles.actionButton}
        text='Delete'
        type='danger'
        disabled={active_list.length === 0 && dir_list.length === 0}
        onPress={_onDeletePress}
      />
      <TextButton
        style={styles.actionButton}
        text='Undelete'
        disabled={delete_list.length === 0 && dir_list.length === 0}
        onPress={_onUndeletePress}
      />
      <TextButton
        style={styles.actionButton}
        text='Download'
        disabled={
          (active_list.length === 0 && dir_active_list.length === 0) ||
          delete_list.length > 0 ||
          dir_inactive_list.length > 0
        }
        onPress={_onDownloadPress}
      />
    </>
  );

  const last_element = path.split('/').filter(_noEmpty).pop();
  const title = last_element ? last_element + '/' : grant_name;
  return (
    <>
      <TitleActionLine title={title}>
        <LinkTextButton
          style={styles.actionButton}
          text='Create Folder'
          url={`/create_folder/${grant_name}${path}`}
        />
        <LinkTextButton
          style={styles.actionButton}
          text='Upload'
          onPress={_onUploadPress}
          url={`/upload/${grant_name}${path}`}
        />
      </TitleActionLine>
      <StandardBoxList
        title='Objects'
        detail={media_list?.length}
        buttons={buttons}
        headers={['Name', 'Size', 'Last Modified']}
        rows={rows}
        selected={selected}
        onChange={setSelected}
      >
        {content}
      </StandardBoxList>
      <BusyOverlay isBusy={is_busy} />
    </>
  );
}
function Deleted(props) {
  return <Text style={styles.deletedText}>{props.text}</Text>;
}
function _filterList(source_dir, path, grant_name, show_deleted, show_active) {
  const low_path = path.toLowerCase();
  const output_list: any[] = [];
  const prefix = path?.length === 1 ? '/' : path;
  const slice_length = prefix.length;
  if (source_dir) {
    const { media_list, dir_list } = source_dir;

    media_list.forEach((media) => {
      const { media_path, is_deleted } = media;
      const show = is_deleted ? show_deleted : show_active;
      if (media_path.toLowerCase().indexOf(low_path) === 0 && show) {
        const name = media_path.slice(slice_length);
        output_list.push({
          name,
          is_dir: false,
          media,
          is_deleted,
          path: `/home/${grant_name}${prefix}${name}`,
        });
      }
    });
    dir_list.forEach((dir) => {
      const { dir_name, file_count } = dir;
      const is_deleted = file_count === 0;
      const show = is_deleted ? show_deleted : show_active;
      if (show) {
        const name = dir_name.slice(slice_length);
        output_list.push({
          name: name + '/',
          is_dir: true,
          media: null,
          is_deleted,
          path: `/home/${grant_name}${prefix}${name}/`,
        });
      }
    });
  }
  output_list.sort(_outputSort);
  return output_list;
}
function _noEmpty(s) {
  return Boolean(s);
}
const LOCALE_OPTS = { sensitivity: 'accent' };
function _outputSort(a, b) {
  const name_diff = a.name.localeCompare(b.name, undefined, LOCALE_OPTS);
  if (a.is_dir && b.is_dir) {
    return name_diff;
  } else if (a.is_dir) {
    return -1;
  } else if (b.is_dir) {
    return 1;
  } else {
    return name_diff;
  }
}
function _downloadFile(url) {
  const a = document.createElement('a');
  a.href = url;
  a.download = url.split('/').pop();
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
}
